import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import { getCourse, enrollInCourse } from '../../services/lmsService';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { toast } from 'sonner';
import Footer from '../Footer';
import {
  Clock, Users, Award, PlayCircle, Video, BookOpen,
  CheckCircle, Calendar, User, ChevronRight,
  GraduationCap, FileText, Globe, Sparkles, ArrowLeft, RefreshCw
} from 'lucide-react';
import { isMobileApp, openWebappWithAuth } from '../../utils/platform';

// Utility function to strip HTML tags from text
const stripHtmlTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, openAuth } = useContext(AuthContext);
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  
  useEffect(() => {
    fetchCourse();
  }, [courseId]);
  
  const fetchCourse = async () => {
    try {
      setLoading(true);
      const response = await getCourse(courseId);
      if (response.success) {
        setCourse(response.data);
        if (response.data.available_batches?.length > 0) {
          setSelectedBatch(response.data.available_batches[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch course:', error);
      toast.error('Course not found');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEnroll = async () => {
    // For mobile app, open webapp with auto-login
    if (isMobileApp()) {
      await openWebappWithAuth(`/courses/${courseId}`);
      return;
    }
    
    if (!user) {
      openAuth('login');
      return;
    }
    
    // Batch is optional - only required if batches exist
    if (course.available_batches?.length > 0 && !selectedBatch) {
      toast.error('Please select a batch');
      return;
    }
    
    try {
      setEnrolling(true);
      const response = await enrollInCourse({
        course_id: course.id,
        batch_id: selectedBatch?.id || null
      });
      
      if (response.success) {
        if (response.data.requires_payment) {
          navigate(`/lms/payment/${response.data.enrollment_id}`);
        } else {
          toast.success('Successfully enrolled!');
          fetchCourse();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };
  
  const getModeIcon = (mode) => {
    switch (mode) {
      case 'live': return <Video className="w-4 h-4 sm:w-5 sm:h-5" />;
      case 'recorded': return <PlayCircle className="w-4 h-4 sm:w-5 sm:h-5" />;
      case 'hybrid': return <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />;
      default: return <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />;
    }
  };
  
  const getDifficultyColor = (level) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }
  
  if (!course) return null;
  
  const isEnrolled = course.user_enrollment !== null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            className="mb-4"
            onClick={() => navigate('/courses')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Courses
          </Button>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Course Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header Card */}
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge className={`${getDifficultyColor(course.difficulty_level)} capitalize`}>
                      {course.difficulty_level}
                    </Badge>
                    <Badge variant="secondary" className="capitalize">
                      {getModeIcon(course.mode)}
                      <span className="ml-1">{course.mode}</span>
                    </Badge>
                    {course.certificate_enabled && (
                      <Badge variant="secondary">
                        <Award className="w-3 h-3 mr-1" />
                        Certificate
                      </Badge>
                    )}
                  </div>
                  
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3">{course.title}</h1>
                  {course.short_description && (
                    <p className="text-gray-600 text-sm sm:text-base mb-4">{stripHtmlTags(course.short_description)}</p>
                  )}
                  
                  {/* Instructor - Show new instructor_name/image or primary_instructor */}
                  {(course.instructor_name || course.primary_instructor) && (
                    <div className="flex items-center gap-3 mb-4">
                      {course.instructor_image ? (
                        <img 
                          src={course.instructor_image} 
                          alt="" 
                          className="w-10 h-10 sm:w-12 sm:h-12 object-cover border border-gray-200" loading="lazy" decoding="async"/>
                      ) : course.primary_instructor?.avatar_url ? (
                        <img 
                          src={course.primary_instructor.avatar_url} 
                          alt="" 
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full" loading="lazy" decoding="async"/>
                      ) : (
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 flex items-center justify-center">
                          <User className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-sm sm:text-base">{course.instructor_name || course.primary_instructor?.full_name}</p>
                        {course.instructor_bio ? (
                          <p className="text-xs sm:text-sm text-gray-500 line-clamp-1">{course.instructor_bio}</p>
                        ) : (
                          <p className="text-xs sm:text-sm text-gray-500">Lead Instructor</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Stats - only show non-empty values */}
                  <div className="flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm text-gray-500">
                    {course.duration_weeks > 0 && (
                      <div className="flex items-center gap-1 sm:gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{course.duration_weeks} weeks</span>
                      </div>
                    )}
                    {course.total_enrolled > 0 && (
                      <div className="flex items-center gap-1 sm:gap-2">
                        <Users className="w-4 h-4" />
                        <span>{course.total_enrolled} students</span>
                      </div>
                    )}
                    {course.total_modules > 0 && (
                      <div className="flex items-center gap-1 sm:gap-2">
                        <BookOpen className="w-4 h-4" />
                        <span>{course.total_modules} modules</span>
                      </div>
                    )}
                    {course.language && (
                      <div className="flex items-center gap-1 sm:gap-2">
                        <Globe className="w-4 h-4" />
                        <span>{course.language}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* About */}
              {course.full_description && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base sm:text-lg">About This Course</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="text-gray-600 text-sm sm:text-base prose prose-sm max-w-none prose-ul:list-disc prose-ol:list-decimal prose-li:my-1"
                      dangerouslySetInnerHTML={{ __html: course.full_description }}
                    />
                  </CardContent>
                </Card>
              )}
              
              {/* What You'll Learn */}
              {course.what_you_learn?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base sm:text-lg">What You&apos;ll Learn</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      {course.what_you_learn.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              
              {/* Syllabus */}
              {course.syllabus?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                      <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
                      Course Syllabus
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Accordion type="single" collapsible className="w-full space-y-4">
                      {course.syllabus.map((module, i) => (
                        <AccordionItem key={module.id} value={module.id} className="border border-gray-200 rounded-lg px-2 shadow-sm overflow-hidden">
                          <AccordionTrigger className="hover:no-underline text-sm sm:text-base py-4 w-full">
                            <div className="flex items-center gap-2 sm:gap-3 text-left w-full min-w-0 pr-2">
                              <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">
                                {i + 1}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-900 break-words">{module.title}</p>
                                <p className="text-xs text-gray-500">
                                  {module.topics?.length || 0} topics
                                </p>
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            {module.description && (
                              <p className="text-gray-600 text-sm mb-3 ml-8 sm:ml-11">{module.description}</p>
                            )}
                            <ul className="space-y-2 ml-8 sm:ml-11">
                              {module.topics?.map((topic) => (
                                <li key={topic.id} className="flex items-center gap-2 text-gray-700 text-sm">
                                  <FileText className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                                  <span>{topic.title}</span>
                                </li>
                              ))}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Enrollment Card - Sticky on Desktop */}
            <div className="lg:col-span-1">
              <Card className="shadow-xl sticky top-4">
                {course.thumbnail_url && (
                  <div className="h-40 sm:h-48 overflow-hidden rounded-t-lg">
                    <img 
                      src={course.thumbnail_url} 
                      alt={course.title}
                      className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                  </div>
                )}
                <CardContent className="p-4 sm:p-6">
                  {/* Price */}
                  {!isMobileApp() && (
                    <div className="mb-4 sm:mb-6">
                      {course.is_free ? (
                        <span className="text-2xl sm:text-3xl font-bold text-green-600">Free</span>
                      ) : (
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                            ₹{(course.discount_price_inr || course.price_inr).toLocaleString()}
                          </span>
                          {course.discount_price_inr && (
                            <span className="text-base sm:text-lg text-gray-400 line-through">
                              ₹{course.price_inr.toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Batch Selection */}
                  {!isEnrolled && course.available_batches?.length > 0 && (
                    <div className="mb-4 sm:mb-6">
                      <label className="text-xs sm:text-sm font-medium text-gray-700 mb-2 block">
                        Select Batch
                      </label>
                      <div className="space-y-2">
                        {course.available_batches.map((batch) => (
                          <div
                            key={batch.id}
                            className={`p-2 sm:p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              selectedBatch?.id === batch.id
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-gray-200 hover:border-indigo-300'
                            }`}
                            onClick={() => setSelectedBatch(batch)}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900 text-sm">{batch.name}</p>
                                <p className="text-xs text-gray-500">
                                  Starts: {new Date(batch.start_date).toLocaleDateString()}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {batch.seats_available} seats
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Enrollment Status or Button */}
                  {isEnrolled ? (
                    <div className="space-y-3 sm:space-y-4">
                      <div className="p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 text-green-700 mb-1 sm:mb-2">
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="font-medium text-sm sm:text-base">Enrolled</span>
                        </div>
                        {course.user_enrollment?.batch_name && (
                          <p className="text-xs sm:text-sm text-green-600">
                            Batch: {course.user_enrollment.batch_name}
                          </p>
                        )}
                      </div>
                      <Button 
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-sm sm:text-base"
                        onClick={() => navigate('/my-learning')}
                      >
                        Go to My Learning
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-base sm:text-lg py-4 sm:py-6"
                      onClick={handleEnroll}
                      disabled={enrolling || (course.available_batches?.length > 0 && !selectedBatch)}
                    >
                      {enrolling ? 'Processing...' : course.is_free ? 'Enroll Now - Free' : 'Enroll Now'}
                    </Button>
                  )}
                  
                  {/* Features */}
                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t space-y-2 sm:space-y-3">
                    {course.recordings_enabled && (
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                        <PlayCircle className="w-4 h-4 text-indigo-500" />
                        <span>Recording access</span>
                      </div>
                    )}
                    {course.certificate_enabled && (
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                        <Award className="w-4 h-4 text-indigo-500" />
                        <span>Certificate on completion</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                      <Calendar className="w-4 h-4 text-indigo-500" />
                      <span>Lifetime resource access</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CourseDetail;
